import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty , get } from 'lodash';
import PropTypes from 'prop-types';
import { useLazyQuery, useMutation } from '@apollo/client';
import { LOUISIANA } from './constants/licenses';
import {
    DEFAULT_FONT_SIZE_SCALE,
    ZOOM_CONFIG,
    IS_MAZE_UNTIMED,
    IS_CAPTIONS_ENABLED,
    IS_ANIMATION_VISUAL_REDUCED
} from './constants/accessibility';
import {
    GET_ACCESSIBILITY_CONFIG,
    SET_ACCESSIBILITY_CONFIG,
    GET_STAFF_CONFIG,
    GET_INSTITUTION_INFO,
} from './common/clients/ApolloServiceClient';
import selectors from './rootSelectors';
import { MAZE } from './constants/measure';

export const ApolloContext = React.createContext({
    fontSizeScaleValue: DEFAULT_FONT_SIZE_SCALE,
    isLouisianaLicenseAvailable: false,
    isMazeUntimed: false,
    isCaptionsEnabled: false,
    isAnimationVisualReduced: false
});

const ApolloContextProvider = ({ children }) => {
    const isLanternUser = useSelector(selectors.getIsLanternUser);
    const studentUid = useSelector(selectors.getUserId);
    const instId = useSelector((state) => selectors.getInstId(state, MAZE.MEASURE));
    const [isLouisianaLicenseAvailable, setLouisianaLicenseAvailability] = useState(false);
    const [fourthWindowLabel, setFourthWindowLabel] = useState('');
    const [accessibilityConfigData, setAccessibilityConfigData] = useState({
        fontSizeScaleValue: DEFAULT_FONT_SIZE_SCALE,
        configBK: null,
        isMazeUntimed: false,
        isCaptionsEnabled: false,
        isAnimationVisualReduced: false,
    });

    const [getStaffConfig, staffConfig] = useLazyQuery(GET_STAFF_CONFIG, {
        onCompleted: (data) => {
            if (!isEmpty(data)) {
                const hasLouisianaLicense = data && data.getStaffConfig && data.getStaffConfig.licenses && data.getStaffConfig.licenses.includes(LOUISIANA);
                setLouisianaLicenseAvailability(hasLouisianaLicense);
            }
        },
        // to handle caching properly
        fetchPolicy: 'cache-and-network',
    });

    const [getInstitutionInfo, institutionInfo] = useLazyQuery(GET_INSTITUTION_INFO, {
        onCompleted: (data) => {
            if (!isEmpty(data)) {
                const windowLabel = get(data, 'getInstitutionInfo_v2.windowDates.fourthWindowLabel', '');
                setFourthWindowLabel(windowLabel);
            }
        },
        // to handle caching properly
        fetchPolicy: 'cache-and-network',
    });

    const [getAccessibilityConfig, accessibilityGetterOptions] = useLazyQuery(GET_ACCESSIBILITY_CONFIG, {
        onCompleted: (data) => {
            if (!isEmpty(data)) {
                const accessibilities = get(data, 'getApplicationConfigResolvers_v2.accessibilityConfig', []);
                const scopeConfig = accessibilities.find((el) => el.studentUid === studentUid);
                // Use config found for specific student
                const fontSizeScaleVal = get(scopeConfig, ZOOM_CONFIG, DEFAULT_FONT_SIZE_SCALE);
                const isCaptionsEnabled = get(scopeConfig, IS_CAPTIONS_ENABLED,false);
                const isMazeUntimed = get(scopeConfig, IS_MAZE_UNTIMED, false);
                const isAnimationVisualReduced = get(scopeConfig, IS_ANIMATION_VISUAL_REDUCED, false);
                // For Mutation using config_bk key if settings were set for the student
                // otherwise we should use undefined as config_bk
                const configBK = get(scopeConfig, 'config_bk', null);

                setAccessibilityConfigData({
                    fontSizeScaleValue: fontSizeScaleVal,
                    isMazeUntimed,
                    isCaptionsEnabled,
                    isAnimationVisualReduced,
                    configBK,
                });
            }
        },
        // to handle caching properly
        fetchPolicy: 'cache-and-network',
    });
    const [setAccessibilityConfig, accessibilitySetterOptions] = useMutation(SET_ACCESSIBILITY_CONFIG);

    const setAccessibilityConfigValues = useCallback(async (configItem, onCompletedCb = () => { }) => {
        setAccessibilityConfigData(prev => {
            const previousState = { ...prev };
            const updatedState = { ...previousState, ...configItem };

            setAccessibilityConfig({
                variables: {
                    studentUid,
                    fontSizeScaleValue: updatedState.fontSizeScaleValue,
                    isCaptionsEnabled: updatedState.isCaptionsEnabled,
                    isAnimationVisualReduced: updatedState.isAnimationVisualReduced,
                    configBK: updatedState.configBK || null,
                }
            }).then((response) => {
                if (response) {
                    onCompletedCb();
                    const configBK = get(response, 'data.postApplicationConfigResolvers_v2.accessibility_bk');
                    setAccessibilityConfigData(current => ({
                        ...current,
                        configBK: configBK || current.configBK,
                    }));
                }
            }).catch((error) => {
                setAccessibilityConfigData(previousState);
                throw error;
            });
            return updatedState;
        });
    }, [setAccessibilityConfig, studentUid]);
    useEffect(() => {
        if (isLanternUser &&
            !staffConfig.loading &&
            isEmpty(staffConfig.data)) {
            getStaffConfig();
        }
    }, [
        isLanternUser,
        staffConfig.data,
        staffConfig.loading,
    ]);

    useEffect(() => {
        if (studentUid && !accessibilityGetterOptions.loading &&
            isEmpty(accessibilityGetterOptions.data)) {
            getAccessibilityConfig({variables: { studentUids: studentUid }});
        }
    }, [
        accessibilityGetterOptions.data,
        accessibilityGetterOptions.loading,
        studentUid,
    ]);

    useEffect(() => {
        if (studentUid && instId && !institutionInfo.loading && isEmpty(institutionInfo.data)) {
            getInstitutionInfo({variables: { instId }});
        }
    }, [
        institutionInfo.data,
        institutionInfo.loading,
        instId,
        studentUid,
    ]);

    const contextValue = useMemo(
        () => ({
            isLouisianaLicenseAvailable,
            fontSizeScaleValue: accessibilityConfigData.fontSizeScaleValue,
            isMazeUntimed: accessibilityConfigData.isMazeUntimed,
            isCaptionsEnabled: accessibilityConfigData.isCaptionsEnabled,
            isAnimationVisualReduced: accessibilityConfigData.isAnimationVisualReduced,
            setAccessibilityConfigValues,
            accessibilityConfigLoading: accessibilitySetterOptions.loading,
            fourthWindowLabelLoading: institutionInfo.loading,
            fourthWindowLabel,
        }),
        [
            isLouisianaLicenseAvailable,
            accessibilityConfigData.fontSizeScaleValue,
            accessibilityConfigData.isMazeUntimed,
            accessibilityConfigData.isCaptionsEnabled,
            accessibilityConfigData.isAnimationVisualReduced,
            setAccessibilityConfigValues,
            accessibilitySetterOptions.loading,
            institutionInfo.loading,
            fourthWindowLabel,
        ]
    );

    return <ApolloContext.Provider value={contextValue}>{children}</ApolloContext.Provider>;
};

ApolloContextProvider.propTypes = {
    children: PropTypes.node,
};

ApolloContextProvider.defaultProps = {
    children: null,
};

export default ApolloContextProvider;
